import {
  WORLD_WIDTH, WORLD_HEIGHT, SHIP_RADIUS, BULLET_RADIUS,
  ASTEROID_VERTEX_COUNT, WORLD_BORDER_LINE_WIDTH
} from 'asteroids-shared/constants'
import { asteroidVertices, toFiniteAsteroidId } from 'asteroids-shared/helpers'
import Camera from '../renderer/Camera.js'
import Starfield from '../renderer/Starfield.js'
import { drawShip, invulnerabilityAlpha } from '../renderer/ShipRenderer.js'
import { drawAsteroid } from '../renderer/AsteroidRenderer.js'
import { drawBullet } from '../renderer/BulletRenderer.js'
import { drawBase, drawFlag } from '../renderer/CtfRenderer.js'
import { drawParticle } from '../renderer/ParticleRenderer.js'
import ClientParticles from '../ClientParticles.js'
import GameAudio from '../audio/GameAudio.js'
import ReplayerPlayback from './Playback.js'
import {
  replayerHtml,
  bindReplayerControls,
  createTransportPlayPauseUpdater,
  updateReplayerVolumeButton,
  createReplayerControlsBinding
} from './InterfaceControls.js'
import { createReplayerKeyboardHandlers } from './KeyboardControls.js'
import { bindReplayerFullscreen, replayerStageIsFullscreen } from './Fullscreen.js'
import {
  syncReplayThruster,
  replayThrustFromInputs,
  playReplayDeathThunder,
  emitReplayStepSounds
} from './ReplayVolume.js'
import {
  createReplayTimelineUiUpdater,
  mountReplayTimelineRipMarkers
} from './ReplayTimeline.js'
import { createReplayLoop } from './ReplayLoop.js'
import { buildReplayShareUrl, REPLAY_SHARE_TOAST_MS, stripReplayQueryParam } from './ReplayShare.js'

/** Thruster direction from replayed ship pose (heading, else velocity). */
function replayThrusterRotDegFromFollow(follow) {
  const r = follow?.rotation
  if (Number.isFinite(r) && Math.abs(r) > 1e-5) return r
  const vx = follow?.vx ?? 0
  const vy = follow?.vy ?? 0
  if (!Number.isFinite(vx) || !Number.isFinite(vy)) return Number.isFinite(r) ? r : 0
  if (vx * vx + vy * vy < .01) return Number.isFinite(r) ? r : 0
  const deg = (Math.atan2(vx, -vy) * 180) / Math.PI
  return ((deg % 360) + 360) % 360
}

function buildFollowOrder(players, gameMode) {
  const list = [...players]
  if (gameMode === 'ctf') {
    const byTeam = new Map()
    for (const p of list) {
      const key = p.teamId != null ? String(p.teamId) : '_'
      if (!byTeam.has(key)) byTeam.set(key, [])
      byTeam.get(key).push(p)
    }
    const keys = [...byTeam.keys()].sort((a, b) => a.localeCompare(b))
    return keys.flatMap(k =>
      (byTeam.get(k) ?? []).sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0))
    )
  }
  return list.sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0))
}

function drawWorldVoid(ctx, camera, toroidalMap) {
  if (toroidalMap) return
  const left = -camera.x
  const top = -camera.y
  const right = left + WORLD_WIDTH
  const bottom = top + WORLD_HEIGHT
  ctx.fillStyle = '#000'
  if (left > 0) ctx.fillRect(0, 0, left, camera.viewH)
  if (right < camera.viewW) ctx.fillRect(right, 0, camera.viewW - right, camera.viewH)
  if (top > 0) ctx.fillRect(0, 0, camera.viewW, top)
  if (bottom < camera.viewH) ctx.fillRect(0, bottom, camera.viewW, camera.viewH - bottom)
}

function drawWorldBorder(ctx, camera, toroidalMap) {
  if (toroidalMap) return
  ctx.save()
  ctx.strokeStyle = '#fff'
  ctx.lineWidth = WORLD_BORDER_LINE_WIDTH
  ctx.strokeRect(-camera.x, -camera.y, WORLD_WIDTH, WORLD_HEIGHT)
  ctx.restore()
}

function collectReplayDom(dlg) {
  const $ = role => {
    const el = dlg.querySelector(`[data-role="${role}"]`)
    if (!el) throw new Error(`replay viewer: missing [data-role="${role}"]`)
    return el
  }
  return {
    replayStage: $('fullscreen-target'),
    canvas: $('surface'),
    trackHit: $('track-hit'),
    trackRail: $('track-rail'),
    trackFill: $('track-fill'),
    playhead: $('playhead'),
    deathMarkersEl: $('death-markers'),
    playPause: $('play-pause'),
    btnRewind: $('rewind'),
    btnFf: $('fast-forward'),
    btnFs: $('fullscreen'),
    followName: $('follow-name'),
    followPrev: $('follow-prev'),
    followNext: $('follow-next'),
    tickCur: $('tick-cur'),
    tickMax: $('tick-max'),
    btnVolume: $('replay-volume'),
    btnShare: $('copy-replay-link'),
    copyReplayToast: $('copy-replay-toast'),
    btnClose: $('close-replay')
  }
}

export function mountReplayer(arrayBuffer, options = {}) {
  const replayIdOpt = options.replayId
  const replayId = replayIdOpt != null && String(replayIdOpt).trim() !== ''
    ? String(replayIdOpt).trim()
    : null

  const dlg = document.createElement('section')
  dlg.className = 'replay-viewer'
  dlg.setAttribute('popover', '')
  dlg.setAttribute('role', 'dialog')
  dlg.setAttribute('aria-label', 'Match replay')
  dlg.innerHTML = replayerHtml()
  const mountParent = options.anchor instanceof HTMLElement
    ? options.anchor
    : (document.querySelector('main') ?? document.body)
  mountParent.appendChild(dlg)

  const {
    replayStage,
    canvas,
    trackHit,
    trackRail,
    trackFill,
    playhead,
    deathMarkersEl,
    playPause,
    btnRewind,
    btnFf,
    btnFs,
    followName,
    followPrev,
    followNext,
    tickCur,
    tickMax,
    btnVolume,
    btnShare,
    copyReplayToast,
    btnClose
  } = collectReplayDom(dlg)
  const ctx2 = canvas.getContext('2d')

  btnShare.hidden = !replayId

  let copyToastTimer = 0
  if (replayId) {
    btnShare.addEventListener('click', () => {
      void (async () => {
        try {
          await navigator.clipboard.writeText(buildReplayShareUrl(replayId))
        } catch (err) {
          console.error('copy replay link failed', err)
          return
        }
        copyReplayToast.setAttribute('data-visible', '')
        copyReplayToast.removeAttribute('aria-hidden')
        clearTimeout(copyToastTimer)
        copyToastTimer = setTimeout(() => {
          copyReplayToast.removeAttribute('data-visible')
          copyReplayToast.setAttribute('aria-hidden', 'true')
          copyToastTimer = 0
        }, REPLAY_SHARE_TOAST_MS)
      })()
    })
  }

  let replayLoop
  const updateTransportButton = createTransportPlayPauseUpdater(playPause, () => replayLoop?.playing ?? false)
  const replayMute = { muted: true }
  function updateVolumeButton() {
    updateReplayerVolumeButton(btnVolume, replayMute.muted)
  }
  const audio = new GameAudio()

  let playback
  try {
    playback = new ReplayerPlayback(arrayBuffer)
  } catch (err) {
    dlg.remove()
    throw err instanceof Error ? err : new Error(String(err))
  }

  const duration = playback.durationTicks
  const gameMode = playback.decoded.gameMode ?? 'coop'
  const followOrder = buildFollowOrder(playback.players, gameMode)
  const followCam = { index: 0 }
  {
    const hideFollowChevrons = followOrder.length <= 1
    followPrev.hidden = hideFollowChevrons
    followNext.hidden = hideFollowChevrons
  }

  const particles = new ClientParticles()
  let thrusterWallAccum = 0
  const partWallClock = { lastMs: performance.now() }
  let particleDtForceOneReplayTick = false
  let replayAliveKnown = false
  const prevAliveById = new Map()
  let lastRenderAppliedSteps = -1
  let lastScrubTickTarget = NaN

  const toroidalMap = playback.decoded.toroidalMap
  const camera = new Camera({
    worldWidth: WORLD_WIDTH,
    worldHeight: WORLD_HEIGHT,
    toroidalMap
  })
  const starfield = new Starfield({ seedText: `replay-${playback.decoded.matchSeed}` })
  const asteroidCache = new Map()

  let dpr = 1

  function layoutCanvas() {
    dpr = Math.min(2, window.devicePixelRatio || 1)
    const inFs = replayerStageIsFullscreen(replayStage)
    const w = inFs ? Math.max(1, Math.floor(replayStage.clientWidth)) : 960
    const h = inFs ? Math.max(1, Math.floor(replayStage.clientHeight)) : 540
    canvas.width = Math.floor(w * dpr)
    canvas.height = Math.floor(h * dpr)
    camera.resize(w, h)
  }

  function reflowStageAndDraw() {
    layoutCanvas()
    render()
  }

  function getAsteroidVertices(rawId, radius) {
    const id = toFiniteAsteroidId(rawId)
    if (id == null || !Number.isFinite(radius)) return null
    const cached = asteroidCache.get(id)
    if (cached?.radius === radius) return cached.vertices
    const vertices = asteroidVertices(id, ASTEROID_VERTEX_COUNT, radius)
    asteroidCache.set(id, { radius, vertices })
    return vertices
  }

  const updateTimelineUi = createReplayTimelineUiUpdater({
    trackFill,
    playhead,
    tickCur,
    tickMax,
    getAppliedSteps: () => playback.appliedSteps,
    durationTicks: duration
  })

  function replayEmitTickCtx() {
    return {
      replayMuted: replayMute.muted,
      toroidalMap,
      audio,
      particles,
      getFollowShip
    }
  }

  function emitTickAudioAndParticles(snapBefore, snapAfter, tickIndex, emitCtx = replayEmitTickCtx(), opts = {}) {
    emitReplayStepSounds(snapBefore, snapAfter, {
      ...emitCtx,
      row: playback.decoded.ticks[tickIndex],
      followOrder,
      followIndex: followCam.index,
      rosterIds: playback.decoded.rosterIds,
      skipAsteroidBurstParticles: opts.skipAsteroidBurstParticles
    })
  }

  function playScrubDeathThunder(snapBefore, snapAfter) {
    const beforeShips = new Map((snapBefore.ships ?? []).map(s => [String(s.id), s]))
    for (const sa of snapAfter.ships ?? []) {
      const wb = beforeShips.get(String(sa.id))
      if (wb?.alive && !sa.alive && Number.isFinite(sa.x) && Number.isFinite(sa.y)) {
        playReplayDeathThunder(audio, replayMute.muted)
      }
    }
  }

  function drawReplayChrome() {
    updateTimelineUi()
    updateTransportButton()
    render()
  }

  function getFollowShip(snap) {
    if (!followOrder.length) return null
    const target = followOrder[followCam.index % followOrder.length]
    const ships = snap.ships ?? []
    const byId = ships.find(s => String(s.id) === String(target.id))
    return byId ?? ships.find(s => s.alive) ?? ships[0] ?? null
  }

  function updateFollowLabel() {
    if (!followOrder.length) {
      followName.textContent = '—'
      return
    }
    const p = followOrder[followCam.index % followOrder.length]
    followName.textContent = p.name || p.id || '—'
  }

  function render() {
    const snap = playback.getSnapshot()
    if (!snap) {
      audio.setThruster(false)
      return
    }

    const applied = playback.appliedSteps
    if (lastRenderAppliedSteps >= 0 && applied < lastRenderAppliedSteps) {
      particles.list.length = 0
      thrusterWallAccum = 0
      replayAliveKnown = false
      prevAliveById.clear()
    }

    for (const s of snap.ships ?? []) {
      const pid = String(s.id)
      const was = prevAliveById.get(pid)
      if (
        replayAliveKnown
        && was === true
        && !s.alive
        && Number.isFinite(s.x)
        && Number.isFinite(s.y)
      ) {
        particles.spawnShipExplosion(s.x, s.y)
        playReplayDeathThunder(audio, replayMute.muted)
      }
      prevAliveById.set(pid, s.alive)
    }
    replayAliveKnown = true

    const nw = performance.now()
    const tickMsPlayback = playback.tickMs
    const dWallRaw = Math.min(nw - partWallClock.lastMs, 100)
    const dWall = Math.min(dWallRaw, tickMsPlayback)
    partWallClock.lastMs = nw
    let simDt = dWall / tickMsPlayback
    if (particleDtForceOneReplayTick) {
      particleDtForceOneReplayTick = false
      simDt = 1
    }
    particles.update(simDt)

    const follow = getFollowShip(snap)
    if (
      follow
      && follow.alive
      && replayThrustFromInputs(playback, follow.id)
    ) {
      thrusterWallAccum += dWall
      let thrusterSpawns = 0
      const rotDeg = replayThrusterRotDegFromFollow(follow)
      const capThrusterWhilePaused = !replayLoop.playing
      while (
        thrusterWallAccum >= tickMsPlayback
        && (!capThrusterWhilePaused || thrusterSpawns < 1)
      ) {
        thrusterWallAccum -= tickMsPlayback
        thrusterSpawns += 1
        particles.spawnThruster({
          position: { x: follow.x, y: follow.y },
          rotation: rotDeg
        })
      }
    } else {
      thrusterWallAccum = 0
    }

    if (follow) {
      camera.follow({ position: { x: follow.x, y: follow.y } })
    }

    const vw = camera.viewW
    const vh = camera.viewH
    ctx2.save()
    ctx2.scale(dpr, dpr)

    ctx2.fillStyle = '#000'
    ctx2.fillRect(0, 0, vw, vh)

    starfield.draw(ctx2, camera)
    drawWorldVoid(ctx2, camera, toroidalMap)
    drawWorldBorder(ctx2, camera, toroidalMap)

    const modeState = snap.modeState
    if (modeState?.kind === 'ctf') {
      for (const base of modeState.bases ?? []) {
        drawBase(ctx2, base, camera)
      }
    }

    for (const a of snap.asteroids ?? []) {
      const radius = a.radius
      if (!Number.isFinite(radius)) continue
      const vertices = getAsteroidVertices(a.id, radius)
      if (!Array.isArray(vertices) || vertices.length < 3) continue
      if (!Number.isFinite(a.x) || !Number.isFinite(a.y)) continue
      drawAsteroid(ctx2, {
        position: { x: a.x, y: a.y },
        rotation: a.rotation ?? 0,
        radius,
        vertices
      }, camera)
    }

    for (const b of snap.bullets ?? []) {
      drawBullet(ctx2, {
        position: { x: b.x, y: b.y },
        radius: BULLET_RADIUS,
        color: '#fff'
      }, camera)
    }

    for (const s of snap.ships ?? []) {
      if (!s.alive) continue
      const thrustVis = replayThrustFromInputs(playback, s.id)
      drawShip(ctx2, {
        position: { x: s.x, y: s.y },
        rotation: s.rotation,
        radius: SHIP_RADIUS,
        alive: true,
        color: s.color,
        name: s.name,
        thrusting: thrustVis,
        thrustingThisTick: thrustVis,
        shieldHealth: s.shieldHealth ?? 0,
        globalAlpha: invulnerabilityAlpha(snap.serverTime, s.invulnerableUntil)
      }, camera)
    }

    if (modeState?.kind === 'ctf' && modeState.flag) {
      const f = modeState.flag
      let drawPos = null
      if (f.carrierId != null) {
        const carrier = (snap.ships ?? []).find(s => String(s.id) === String(f.carrierId) && s.alive)
        if (carrier) drawPos = { x: carrier.x, y: carrier.y - 24 }
      } else if (Number.isFinite(f.x) && Number.isFinite(f.y)) {
        drawPos = { x: f.x, y: f.y }
      }
      if (drawPos) drawFlag(ctx2, { ...f, x: drawPos.x, y: drawPos.y }, camera)
    }

    if (!trackHit.hasAttribute('data-scrubbing')) {
      for (const p of particles.list) drawParticle(ctx2, p, camera)
    }

    ctx2.restore()
    syncReplayThruster({ audio, replayMuted: replayMute.muted, playback, getFollowShip })
    lastRenderAppliedSteps = applied
  }

  function resetReplayParticles(o = {}) {
    if (o.clearScrubTickDedupe) lastScrubTickTarget = NaN
    particles.list.length = 0
    thrusterWallAccum = 0
    lastRenderAppliedSteps = -1
    replayAliveKnown = false
    prevAliveById.clear()
    playback.world?.setPostGameVisual(false)
  }

  function scrubPlaybackToTick(targetTick) {
    const n = Math.max(0, Math.min(Math.floor(Number(targetTick) || 0), duration))
    if (n === lastScrubTickTarget && playback.appliedSteps === n) {
      updateTimelineUi()
      return
    }
    lastScrubTickTarget = n
    resetReplayParticles()
    playback.seek(0)
    const tickEmitCtx = replayEmitTickCtx()
    for (let i = 0; i < n; i++) {
      const snapBefore = playback.getSnapshot()
      playback.stepTick(i)
      const snapAfter = playback.getSnapshot()
      emitTickAudioAndParticles(snapBefore, snapAfter, i, tickEmitCtx, {
        skipAsteroidBurstParticles: true
      })
      playScrubDeathThunder(snapBefore, snapAfter)
    }
    const snap = playback.getSnapshot()
    prevAliveById.clear()
    for (const s of snap?.ships ?? []) {
      prevAliveById.set(String(s.id), s.alive)
    }
    replayAliveKnown = true
    thrusterWallAccum = playback.tickMs
    partWallClock.lastMs = performance.now()
    drawReplayChrome()
  }

  function stepFrameForward() {
    replayLoop.pausePlayback()
    if (playback.appliedSteps >= duration) return
    const idx = playback.appliedSteps
    const snapBefore = playback.getSnapshot()
    playback.stepTick(idx)
    const snapAfter = playback.getSnapshot()
    emitTickAudioAndParticles(snapBefore, snapAfter, idx)
    partWallClock.lastMs = performance.now()
    thrusterWallAccum += playback.tickMs
    particleDtForceOneReplayTick = true
    drawReplayChrome()
  }

  function stepFrameBack() {
    replayLoop.pausePlayback()
    const next = Math.max(0, playback.appliedSteps - 1)
    if (next === playback.appliedSteps) return
    if (next > 0) {
      playback.seek(next - 1)
      const snapPrev = playback.getSnapshot()
      prevAliveById.clear()
      for (const s of snapPrev.ships ?? []) {
        prevAliveById.set(String(s.id), s.alive)
      }
      replayAliveKnown = true
      playback.seek(next)
    } else {
      prevAliveById.clear()
      replayAliveKnown = false
      playback.seek(0)
    }
    thrusterWallAccum = 0
    partWallClock.lastMs = performance.now()
    playback.world?.setPostGameVisual(false)
    drawReplayChrome()
  }

  replayLoop = createReplayLoop({
    playback,
    duration,
    particles,
    audio,
    getFollowShip,
    followOrder,
    getFollowIndex: () => followCam.index,
    getReplayMuted: () => replayMute.muted,
    toroidalMap,
    render,
    updateTimelineUi,
    updateTransportButton,
    resetReplayParticles,
    btnRewind,
    btnFf
  })

  const disposeControls = bindReplayerControls(createReplayerControlsBinding({
    canvas,
    playPause,
    btnRewind,
    btnFf,
    trackHit,
    trackRail,
    followPrev,
    followNext,
    btnVolume,
    btnClose,
    playback,
    duration,
    replayLoop,
    audio,
    updateTransportButton,
    updateTimelineUi,
    resetReplayParticles,
    scrubPlaybackToTick,
    render,
    followOrder,
    followCam,
    updateFollowLabel,
    replayMute,
    updateVolumeButton,
    getFollowShip,
    hidePopover: () => dlg.hidePopover()
  }))

  const { updateFullscreenButton, dispose: disposeFullscreen } = bindReplayerFullscreen({
    replayStage,
    btnFs,
    onLayoutAfterChange: reflowStageAndDraw
  })

  function onResize() {
    reflowStageAndDraw()
  }

  resetReplayParticles()
  playback.seek(0)
  layoutCanvas()
  mountReplayTimelineRipMarkers(deathMarkersEl, playback.decoded.deaths ?? [], duration, gameMode, playback.players)
  updateFollowLabel()
  updateFullscreenButton()
  drawReplayChrome()

  const { keydown: onKey, keyup: onKeyUp, blur: onWinBlur } = createReplayerKeyboardHandlers({
    dialog: dlg,
    playPause,
    btnFs,
    btnRewind,
    btnFf,
    followPrev,
    followNext,
    replayLoop,
    resetReplayParticles,
    playback,
    duration,
    updateTimelineUi,
    updateTransportButton,
    render,
    followOrder,
    audio,
    stepFrameBack,
    stepFrameForward
  })

  dlg.addEventListener('toggle', e => {
    if (e.newState !== 'closed') return
    clearTimeout(copyToastTimer)
    copyToastTimer = 0
    stripReplayQueryParam()
    replayLoop.stopLoop()
    audio.destroy()
    disposeFullscreen()
    disposeControls()
    window.removeEventListener('keydown', onKey, true)
    window.removeEventListener('keyup', onKeyUp, true)
    window.removeEventListener('blur', onWinBlur)
    window.removeEventListener('resize', onResize)
    if (document.fullscreenElement) void document.exitFullscreen()
    dlg.remove()
  })

  window.addEventListener('keydown', onKey, true)
  window.addEventListener('keyup', onKeyUp, true)
  window.addEventListener('blur', onWinBlur)
  window.addEventListener('resize', onResize)

  dlg.showPopover()
}
